import './index.css'
import cn from 'clsx'

import React, { Component } from 'react'
import TabContent from './content'
import TabNav from './nav'
import TabStepsNav from './steps-nav'
import Tab from './tab'
export { Tab, TabContent, TabNav, TabStepsNav }
type Props = {
  children?: React.ReactNode
  className?: string
  index?: number
  onChange?: (...args: Array<any>) => any
}
/**
 * @deprecated Use `Tabs` from `components/ds` instead.
 * @see {@link components/ds/Tabs}
 * @see https://www.radix-ui.com/themes/docs/components/tabs
 * @migration
 * - Replace legacy Tabs with Radix Tabs compound component from components/ds:
 *   ```tsx
 *   // Legacy
 *   <Tabs index={activeTab} onChange={setActiveTab}>
 *     <Tab header="Tab 1">Content 1</Tab>
 *     <Tab header="Tab 2">Content 2</Tab>
 *   </Tabs>
 *
 *   // DS alternative
 *   import { Tabs } from 'components/ds'
 *
 *   <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
 *     <Tabs.List>
 *       <Tabs.Trigger value="tab1">Tab 1</Tabs.Trigger>
 *       <Tabs.Trigger value="tab2">Tab 2</Tabs.Trigger>
 *     </Tabs.List>
 *     <Tabs.Content value="tab1">Content 1</Tabs.Content>
 *     <Tabs.Content value="tab2">Content 2</Tabs.Content>
 *   </Tabs.Root>
 *   ```
 * - Prop mapping:
 *   - index → value (use string identifiers instead of numeric indices)
 *   - onChange(index, alias) → onValueChange(value)
 *   - className → className on Tabs.Root
 * - Tab/TabNav/TabContent sub-components map to Tabs.Trigger/Tabs.List/Tabs.Content
 *
 * A tabs component that manages tabbed content with navigation
 * @param children - The Tab components to render
 * @param className - Optional CSS class name for the container
 * @param index - The currently active tab index (defaults to 0)
 * @param onChange - Callback called when the active tab changes
 * @returns A tabs container with navigation and content area
 */
export default class Tabs extends Component<Props> {
  static defaultProps: {
    index: number
  } = {
    index: 0,
  }
  _handleHeaderClick: (arg0: any) => (arg0: React.MouseEvent<any>) => void =
    (alias: any) => event => {
      const el = event.currentTarget
      const idx = parseInt(el.id, 10)

      if (this.props.onChange) {
        this.props.onChange(idx, alias)
      }
    }

  _parseChildren(): {
    contents: Array<React.ReactElement<any>>
    headers: Array<any>
  } {
    const headers = []
    const contents = []
    React.Children.forEach<React.ReactElement<any>>(
      this.props.children as React.ReactElement<any>[],
      item => {
        if (!item) return
        const { children, ...props } = item.props
        headers.push(props)

        if (children) {
          contents.push(<TabContent>{children}</TabContent>)
        }
      },
    )
    return {
      headers,
      contents,
    }
  }

  _renderHeaders: (headers: Array<any>) => Array<any> = (
    headers: any[],
  ): Record<string, any>[] =>
    headers.map((item, idx) => {
      return {
        ...item,
        id: idx,
        key: idx,
        active: this.props.index === idx,
        onClick: this._handleHeaderClick(item.alias),
      }
    })

  _renderContents(
    contents: React.ReactElement<any>[],
  ): React.ReactElement<any> {
    const tabIndex = contents.findIndex((item, idx) => this.props.index === idx)

    if (contents && contents[tabIndex]) {
      return React.cloneElement(contents[tabIndex], {
        key: tabIndex,
        active: true,
        tabIndex,
      })
    }
  }

  render(): React.ReactElement<'div'> {
    const { className } = this.props

    const { headers, contents } = this._parseChildren()

    return (
      <div className={cn('Tabs', className)}>
        <TabNav items={this._renderHeaders(headers)} />
        {this._renderContents(contents)}
      </div>
    )
  }
}
